import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { alpha, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	drawMarks,
	isMarkTooSmall,
	Mark,
	MarkKind,
	MarkPalette,
	Point,
} from "./support-annotation-marks";
import { fullScreenPanelQuery } from "./support-panel-layout";

interface SupportAnnotatorDialogProps {
	/** The picture being marked up; null keeps the dialog closed. */
	image: Blob | null;
	/** The note this picture already carries, so reopening it keeps it. */
	initialCaption: string;
	onCancel: () => void;
	/**
	 * Called with ONE flattened PNG — the marks are baked into the pixels —
	 * and the note written next to it. The note belongs here rather than only
	 * in the panel: what a mark means is clearest while it is being drawn.
	 *
	 * The picture is already attached by the time this opens; this only keeps
	 * what was drawn on it, which is why the button says Done and not Attach.
	 */
	onAttach: (flattened: Blob, caption: string) => void;
}

const TOOLS: { kind: MarkKind; label: string; icon: React.ReactElement }[] = [
	{ kind: "pen", label: "Draw freehand", icon: <EditIcon fontSize="small" /> },
	{
		kind: "highlight",
		label: "Highlight — dims everything else",
		icon: <HighlightAltIcon fontSize="small" />,
	},
	{
		kind: "hide",
		label: "Hide — paints over the pixels before upload",
		icon: <VisibilityOffIcon fontSize="small" />,
	},
];

/**
 * The red pen: a canvas showing the captured picture with a drawing overlay.
 * Attach composites picture and marks into a single PNG — nothing re-edits a
 * mark afterwards, so the unmarked original is deliberately discarded. That is
 * what makes Hide a real redaction rather than a sticker over the evidence.
 */
const SupportAnnotatorDialog: React.FC<SupportAnnotatorDialogProps> = ({
	image,
	initialCaption,
	onCancel,
	onAttach,
}) => {
	const theme = useTheme();
	// One string, used as both the hook's question and the sx key below, so the
	// layout and the `fullScreen` prop cannot answer differently.
	const fullScreenQuery = fullScreenPanelQuery(theme);
	const isFullScreen = useMediaQuery(fullScreenQuery);
	const baseCanvasRef = useRef<HTMLCanvasElement>(null);
	const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
	const [marks, setMarks] = useState<Mark[]>([]);
	const [draft, setDraft] = useState<Mark | null>(null);
	const [picture, setPicture] = useState<HTMLImageElement | null>(null);
	const [caption, setCaption] = useState("");
	const [tool, setTool] = useState<MarkKind>("pen");
	const [size, setSize] = useState<{ width: number; height: number } | null>(null);

	const palette: MarkPalette = useMemo(
		() => ({
			pen: theme.palette.error.main,
			highlight: theme.palette.warning.main,
			hide: theme.palette.grey[900],
			dim: alpha(theme.palette.common.black, 0.5),
		}),
		[theme]
	);

	// Decode whatever picture arrives; its size is what mounts the canvases.
	useEffect(() => {
		setMarks([]);
		setDraft(null);
		setCaption(initialCaption);
		if (!image) {
			setPicture(null);
			setSize(null);
			return;
		}
		const url = URL.createObjectURL(image);
		const decoded = new Image();
		decoded.onload = () => {
			URL.revokeObjectURL(url);
			setSize({ width: decoded.naturalWidth, height: decoded.naturalHeight });
			setPicture(decoded);
		};
		decoded.src = url;
	}, [image, initialCaption]);

	// Paint it in a second effect rather than a requestAnimationFrame: the
	// canvas only exists once `size` has been committed, and a blob URL decodes
	// fast enough to beat that commit — which left the picture blank.
	useEffect(() => {
		const canvas = baseCanvasRef.current;
		const context = canvas?.getContext("2d");
		if (!picture || !size || !canvas || !context) return;
		context.drawImage(picture, 0, 0);
	}, [picture, size]);

	// Redraw every mark whenever they change.
	useEffect(() => {
		const canvas = overlayCanvasRef.current;
		const context = canvas?.getContext("2d");
		if (!canvas || !context || !size) return;
		context.clearRect(0, 0, canvas.width, canvas.height);
		drawMarks(
			context,
			draft ? [...marks, draft] : marks,
			size.width,
			size.height,
			palette
		);
	}, [marks, draft, size, palette]);

	const canvasPoint = useCallback((event: React.PointerEvent): Point | null => {
		const canvas = overlayCanvasRef.current;
		if (!canvas) return null;
		const rect = canvas.getBoundingClientRect();
		return {
			x: (event.clientX - rect.left) * (canvas.width / rect.width),
			y: (event.clientY - rect.top) * (canvas.height / rect.height),
		};
	}, []);

	const onPointerDown = (event: React.PointerEvent) => {
		const point = canvasPoint(event);
		if (!point) return;
		event.currentTarget.setPointerCapture(event.pointerId);
		setDraft(
			tool === "pen"
				? { kind: "pen", points: [point] }
				: { kind: tool, from: point, to: point }
		);
	};

	const onPointerMove = (event: React.PointerEvent) => {
		if (!draft) return;
		const point = canvasPoint(event);
		if (!point) return;
		setDraft(
			draft.kind === "pen"
				? { kind: "pen", points: [...draft.points, point] }
				: { ...draft, to: point }
		);
	};

	const onPointerUp = (event: React.PointerEvent) => {
		event.currentTarget.releasePointerCapture(event.pointerId);
		if (!draft || !size) return;
		if (!isMarkTooSmall(draft, size.width)) {
			setMarks((previous) => [...previous, draft]);
		}
		setDraft(null);
	};

	const attach = () => {
		const base = baseCanvasRef.current;
		const overlay = overlayCanvasRef.current;
		const context = base?.getContext("2d");
		if (!base || !overlay || !context) return;
		context.drawImage(overlay, 0, 0);
		base.toBlob((flattened) => {
			if (flattened) onAttach(flattened, caption.trim());
		}, "image/png");
	};

	return (
		<Dialog
			open={image !== null}
			onClose={onCancel}
			maxWidth={false}
			fullScreen={isFullScreen}
			data-component="support-annotator-dialog"
			slotProps={{
				paper: {
					sx: {
						// The picture is the point: give it the monitor it is
						// being looked at on rather than a fixed dialog width.
						width: "96vw",
						// dvh rather than vh: on iOS Safari `vh` is the LARGE
						// viewport, the one that runs on behind the bottom
						// toolbar — which is where the Done button went.
						height: "94dvh",
						maxWidth: "none",
						maxHeight: "none",
						m: 0,
						// On a genuinely small screen it stops being a card
						// floating in a backdrop and becomes the screen, the
						// same query the report panel answers to.
						[fullScreenQuery]: {
							width: "100%",
							maxWidth: "100%",
							height: "100dvh",
							maxHeight: "100dvh",
							borderRadius: 0,
						},
					},
				},
			}}
		>
			<DialogTitle>Mark what you mean</DialogTitle>
			<DialogContent
				sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
			>
				{/* useFlexGap so wrapping rows keep their spacing — Stack's
				    default margin-based spacing does not survive a wrap. */}
				<Stack
					direction="row"
					spacing={1}
					useFlexGap
					flexWrap="wrap"
					alignItems="center"
					sx={{ mb: 1 }}
				>
					<ToggleButtonGroup
						exclusive
						size="small"
						value={tool}
						onChange={(_event, next: MarkKind | null) =>
							next && setTool(next)
						}
						data-component="support-annotator-tools"
					>
						{TOOLS.map((entry) => (
							<ToggleButton
								key={entry.kind}
								value={entry.kind}
								data-component={`support-annotator-${entry.kind}`}
							>
								<Tooltip title={entry.label}>{entry.icon}</Tooltip>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
					<IconButton
						size="small"
						onClick={() => setMarks((previous) => previous.slice(0, -1))}
						data-component="support-annotator-undo"
					>
						<UndoIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setMarks([])}
						data-component="support-annotator-clear"
					>
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
					<Typography variant="body2" color="text.secondary">
						Draw on the picture — the marks are saved into it, and Hide
						removes what it covers.
					</Typography>
				</Stack>
				{size && (
					// Centre the picture in whatever room is left and let it grow
					// to fill it, capped by both sides so a wide capture on a tall
					// dialog letterboxes instead of overflowing. The inner box
					// shrink-wraps the scaled canvas, which is what keeps the
					// drawing overlay exactly on top of it.
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							// The inner box's maxHeight is a percentage of a
							// parent whose height comes from flex, which the
							// canvas can outgrow — it then paints over the Note
							// field below. Clip here so it cannot.
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								position: "relative",
								display: "inline-block",
								border: 1,
								borderColor: "divider",
								lineHeight: 0,
								maxWidth: "100%",
								maxHeight: "100%",
							}}
						>
							<canvas
								ref={baseCanvasRef}
								width={size.width}
								height={size.height}
								style={{
									display: "block",
									maxWidth: "100%",
									maxHeight: "100%",
									width: "auto",
									height: "auto",
								}}
							/>
							<canvas
								ref={overlayCanvasRef}
								width={size.width}
								height={size.height}
								onPointerDown={onPointerDown}
								onPointerMove={onPointerMove}
								onPointerUp={onPointerUp}
								data-component="support-annotator-canvas"
								style={{
									position: "absolute",
									inset: 0,
									width: "100%",
									height: "100%",
									cursor: "crosshair",
									touchAction: "none",
								}}
							/>
						</Box>
					</Box>
				)}
				{/* "Note", not a question. It sits under the picture in the
				    strip afterwards, and a field named for what it produces is
				    easier to recognise there than one phrased as a prompt. */}
				<TextField
					variant="standard"
					fullWidth
					label="Note"
					value={caption}
					onChange={(event) => setCaption(event.target.value)}
					sx={{ mt: 1.5, flexShrink: 0 }}
					data-component="support-annotator-caption"
				/>
			</DialogContent>
			<DialogActions data-component="support-annotator-actions">
				<Button onClick={onCancel} data-component="support-annotator-cancel">
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={attach}
					data-component="support-annotator-attach"
				>
					Done
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SupportAnnotatorDialog;
